import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download } from 'lucide-react';
import type { EntityRow } from './types';

interface AggTableProps {
  data: EntityRow[];
  entityLabel: string;
  onDrillDown?: (name: string) => void;
  hideRoRank?: boolean;
  hideNationalRank?: boolean;
}

const AggTable: React.FC<AggTableProps> = function ({ data, entityLabel, onDrillDown, hideRoRank, hideNationalRank }) {
  const [sortKey, setSortKey] = useState<keyof EntityRow>('passPercentage');
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState('');
  const [topBottomMode, setTopBottomMode] = useState<'none' | 'top' | 'bottom'>('none');
  const [topBottomN, setTopBottomN] = useState('10');
  const [passMin, setPassMin] = useState('');
  const [passMax, setPassMax] = useState('');
  const [piMin, setPiMin]     = useState('');
  const [piMax, setPiMax]     = useState('');

  const avgPass = data.length
    ? data.reduce(function (s, r) { return s + r.passPercentage; }, 0) / data.length
    : 0;
  const avgPI = data.length
    ? data.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / data.length
    : 0;

  function handleSort(k: keyof EntityRow) {
    if (sortKey === k) { setSortAsc(!sortAsc); } else { setSortKey(k); setSortAsc(false); }
  }

  // When primary sort is PI or Pass%, the other automatically becomes the secondary sort (same direction)
  const linkedSecKey: keyof EntityRow | null =
    sortKey === 'performanceIndex' ? 'passPercentage' :
    sortKey === 'passPercentage'   ? 'performanceIndex' : null;

  // 1. Search + sort
  const sortedFiltered = [...data]
    .filter(function (r) { return r.name.toLowerCase().includes(search.toLowerCase()); })
    .sort(function (a, b) {
      const av = a[sortKey]; const bv = b[sortKey];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = sortAsc ? av - bv : bv - av;
      else cmp = sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      if (cmp !== 0) return cmp;
      if (linkedSecKey) {
        const as2 = a[linkedSecKey]; const bs2 = b[linkedSecKey];
        if (typeof as2 === 'number' && typeof bs2 === 'number') return sortAsc ? as2 - bs2 : bs2 - as2;
      }
      return 0;
    });

  // 2. Pass% and PI range filters
  const rangeFiltered = sortedFiltered.filter(function (r) {
    const pm = passMin !== '' ? r.passPercentage    >= Number(passMin) : true;
    const px = passMax !== '' ? r.passPercentage    <= Number(passMax) : true;
    const im = piMin   !== '' ? r.performanceIndex  >= Number(piMin)   : true;
    const ix = piMax   !== '' ? r.performanceIndex  <= Number(piMax)   : true;
    return pm && px && im && ix;
  });

  // 3. Top / Bottom N slice
  const n = Math.max(1, parseInt(topBottomN, 10) || 10);
  const displayRows = topBottomMode === 'top'    ? rangeFiltered.slice(0, n)
                    : topBottomMode === 'bottom' ? rangeFiltered.slice(-n)
                    : rangeFiltered;

  function downloadCSV() {
    const hasCount  = data.length > 0 && data[0].count !== undefined;
    const hasRoRank = !hideRoRank && data.length > 0 && data[0].roRank !== undefined;
    const headers: string[] = [];
    if (!hideNationalRank) headers.push('Natl. Rank');
    headers.push(entityLabel);
    if (hasCount)  headers.push('KVs');
    if (hasRoRank) headers.push('RO Rank');
    headers.push('Appeared', 'Passed', 'Compartment', 'Absent', 'Pass%', 'PI');
    const rows = displayRows.map(function (r) {
      const cells: (string | number)[] = [];
      if (!hideNationalRank) cells.push(r.nationalRank ?? '');
      cells.push(r.name);
      if (hasCount)  cells.push(r.count ?? '');
      if (hasRoRank) cells.push(r.roRank ?? '');
      cells.push(r.totalAppeared, r.totalPassed, r.totalCompartment, r.totalAbsent, r.passPercentage, r.performanceIndex);
      return cells.map(function (v) { return '"' + String(v).replace(/"/g, '""') + '"'; }).join(',');
    });
    const csv  = [headers.map(function (h) { return '"' + h + '"'; }).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = entityLabel.toLowerCase() + '-performance.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  function Th(p: { label: string; field: keyof EntityRow }) {
    const isPrimary = sortKey === p.field;
    const isSecondary = linkedSecKey === p.field;
    const SortIcon = isPrimary || isSecondary
      ? (sortAsc ? ArrowUp : ArrowDown)
      : ArrowUpDown;
    const iconClass = isPrimary
      ? 'w-3 h-3 text-[#F36B21]'
      : isSecondary
        ? 'w-3 h-3 text-orange-300'
        : 'w-3 h-3 text-gray-400';
    return (
      <th
        className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
        onClick={function () { handleSort(p.field); }}
        title={isSecondary ? 'Co-sorted with ' + (p.field === 'passPercentage' ? 'PI' : 'Pass%') : undefined}
      >
        <span className="flex items-center gap-1">
          {p.label}
          <SortIcon className={iconClass} />
        </span>
      </th>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
        <h3 className="text-lg font-semibold text-gray-800">{entityLabel}-wise Performance</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{data.length} {entityLabel.toLowerCase()}s</span>
          <div className="relative w-full md:w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder={'Search ' + entityLabel.toLowerCase() + '…'} value={search}
              onChange={function (e) { setSearch(e.target.value); }}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
        </div>
      </div>

      {/* Filter bar: Top/Bottom N · Pass% range · PI range · CSV */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 py-2.5 px-3 bg-gray-50 rounded-lg border border-gray-100 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-gray-500 font-medium">Show:</span>
          <div className="flex rounded overflow-hidden border border-gray-200">
            {(['none', 'top', 'bottom'] as const).map(function (m) {
              return (
                <button key={m} onClick={function () { setTopBottomMode(m); }}
                  className={'px-2 py-1 transition-colors ' + (topBottomMode === m ? 'bg-[#F36B21] text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}>
                  {m === 'none' ? 'All' : m === 'top' ? 'Top' : 'Bottom'}
                </button>
              );
            })}
          </div>
          <input type="number" min={1} value={topBottomN} onChange={function (e) { setTopBottomN(e.target.value); }}
            disabled={topBottomMode === 'none'}
            className="w-12 px-1.5 py-1 rounded border border-gray-200 text-center disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-orange-300" />
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">Pass%:</span>
          <input type="number" placeholder="min" value={passMin} onChange={function (e) { setPassMin(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
          <span className="text-gray-400">–</span>
          <input type="number" placeholder="max" value={passMax} onChange={function (e) { setPassMax(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">PI:</span>
          <input type="number" placeholder="min" value={piMin} onChange={function (e) { setPiMin(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
          <span className="text-gray-400">–</span>
          <input type="number" placeholder="max" value={piMax} onChange={function (e) { setPiMax(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
        </div>
        <div className="flex-1" />
        <span className="text-gray-400">{displayRows.length} row{displayRows.length !== 1 ? 's' : ''}</span>
        <button onClick={downloadCSV}
          className="flex items-center gap-1 px-2.5 py-1 rounded bg-[#F36B21] text-white font-medium hover:bg-orange-600 transition-colors">
          <Download className="w-3 h-3" />CSV
        </button>
      </div>
      <div className="overflow-auto max-h-[520px]">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-gray-50">
              {!hideNationalRank && <Th label="Natl. Rank" field="nationalRank" />}
              <Th label={entityLabel} field="name" />
              {data.length > 0 && data[0].count !== undefined && <Th label="KVs" field="count" />}
              {!hideRoRank && data.length > 0 && data[0].roRank !== undefined && <Th label="RO Rank" field="roRank" />}
              <Th label="Appeared" field="totalAppeared" />
              <Th label="Passed" field="totalPassed" />
              <Th label="Comp." field="totalCompartment" />
              <Th label="Absent" field="totalAbsent" />
              <Th label={'Pass % (avg ' + avgPass.toFixed(1) + '%)'} field="passPercentage" />
              <Th label={'PI (avg ' + avgPI.toFixed(1) + ')'} field="performanceIndex" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayRows.map(function (row) {
              return (
                <tr
                  key={row.name}
                  className={
                    'transition-colors ' +
                    (onDrillDown ? 'hover:bg-orange-50 cursor-pointer' : '')
                  }
                  onClick={function () { if (onDrillDown) onDrillDown(row.name); }}
                >
                  {!hideNationalRank && <td className="py-3 px-4 text-sm text-gray-500 font-medium">{row.nationalRank ?? '—'}</td>}
                  <td className="py-3 px-4 text-sm font-medium text-gray-800">{row.name}</td>
                  {data.length > 0 && data[0].count !== undefined && (
                    <td className="py-3 px-4 text-sm text-gray-600">{row.count}</td>
                  )}
                  {!hideRoRank && data.length > 0 && data[0].roRank !== undefined && (
                    <td className="py-3 px-4 text-sm text-gray-700 font-medium">{row.roRank ?? '—'}</td>
                  )}
                  <td className="py-3 px-4 text-sm text-gray-700">{row.totalAppeared.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-green-700 font-medium">{row.totalPassed.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-orange-600">{row.totalCompartment}</td>
                  <td className="py-3 px-4 text-sm text-blue-600">{row.totalAbsent}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={
                      'px-2 py-1 rounded-full text-xs font-semibold ' +
                      (row.passPercentage >= avgPass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
                    }>
                      {row.passPercentage}%
                    </span>
                  </td>
                  <td className={
                    'py-3 px-4 text-sm font-medium ' +
                    (row.performanceIndex >= avgPI ? 'text-green-700' : 'text-red-600')
                  }>
                    {row.performanceIndex}
                  </td>
                </tr>
              );
            })}
            {displayRows.length === 0 && (
              <tr>
                <td colSpan={12} className="py-8 text-center text-gray-400">No data found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AggTable;
